<!-- BaseReport.vue -->
<script setup lang="ts">
import {onMounted, ref} from "vue";
import ReportPeriodFilterBar from "@/components/Reporting/ReportPeriodFilterBar.vue";
import { useReportingStore } from "@/store/reporting";
import {downloadExportedData, type ExportedData} from "@/lib/fileDownload";

const reportingStore = useReportingStore();

const props = defineProps({
  metricEndpoint: {
    type: String,
    required: true
  },
  additionalMetricEndpoint: {
    type: String,
    required: false
  },
  dataEndpoint: {
    type: String,
    required: true
  }
});

const emit = defineEmits(["data-loaded"]);

const showReport = ref(false);
const activeGroup = ref("tenant");
const tableData = ref();
const metric = ref();
const additionalMetric = ref();
const loading = ref(false);

onMounted(() => {
  reportingStore.resetFilters();
  resetReport();
});

async function runReport() {
  loading.value = true;
  resetReport();
  setActiveGroup();
  await fetchReportData();
  await fetchMetricData();
  await fetchAdditionalMetric();
  showReport.value = true;
  loading.value = false;
  emit("data-loaded", { tableData: tableData.value, metric: metric.value });
}

function setActiveGroup() {
  if (reportingStore.isCohortFilteringActive)
    activeGroup.value = "cohort";
  else if (reportingStore.isInstitutionFilteringActive)
    activeGroup.value = "institution";
  else activeGroup.value = "tenant";
}

function resetReport() {
  activeGroup.value = "tenant";
  metric.value = undefined;
  showReport.value = false;
  tableData.value = [];
}

async function fetchMetricData() {
  metric.value = await reportingStore.fetchReport(props.metricEndpoint);
}

async function fetchAdditionalMetric() {
  if (props.additionalMetricEndpoint)
    additionalMetric.value = await reportingStore.fetchReport(props.additionalMetricEndpoint);
}

async function fetchReportData(groupBy = activeGroup.value) {
  tableData.value = await reportingStore.fetchReport(props.dataEndpoint, { groupBy });
}

async function handleGroupChange(group: string) {
  activeGroup.value = group;
  await fetchReportData(group);
}

async function exportReport(format: "csv" | "excel") {
  const exportData = await reportingStore.fetchReport(props.dataEndpoint, {
    groupBy: activeGroup.value,
    exportFormat: format
  });
  downloadExportedData(exportData as unknown as ExportedData);
}
</script>

<template>
  <ReportPeriodFilterBar
    :show-filters="true"
    :show-time-frame="false"
    :show-time-frame-switch="true"
    :show-run-report-button="true"
    :loading="loading"
    @run-report="runReport"
    @reset-filters="resetReport"
  />
  <template v-if="showReport">
    <slot name="metric-content" :metric-data="metric" :additional-metric-data="additionalMetric" />
    <!-- Provide slot for the report table or other content -->
    <slot
      name="report-content"
      :table-data="tableData"
      :handle-group-change="handleGroupChange"
      :export-report="exportReport"
      :active-group="activeGroup"
    />
  </template>
</template>
